import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCrmResource } from "../../../hooks/useCrmResource";
import {
  getPersonProfile,
  personAction,
  searchPersonLeads,
  PersonActionInput,
  PersonMemberView,
  PersonProfileView,
  PersonSearchLead,
} from "../../../api/crmPerson";
import { CrmHttpError, describeCrmError } from "../../../api/crm";
import { CrmConnectGate } from "../../../context/CrmSessionContext";
import Section from "./Section";
import Pill from "./Pill";

/**
 * Panel "Perfil de persona (CRM)" del aside de la ficha del lead. Espejo del
 * PersonProfileBlock del CRM: agrupa los leads que el CRM ha vinculado a la
 * misma persona (multi-quote aparente por +tag/noning) y sus relacionados/
 * familiares, con curación de sugerencias (confirmar/rechazar/promocionar) y
 * "vincular otro lead" manual.
 *
 * GET /leads/:id/person devuelve null si el lead no pertenece a ningún perfil —
 * caso mayoritario, NO error: se ofrece igualmente el formulario de vincular
 * (attach crea el perfil con este lead como ancla). Los leadId de los miembros
 * llegan ya canonicalizados por la capa API (src/api/crmPerson.ts), así que
 * comparan directo contra el leadUlid de la ruta y linkan a /leads/:leadUlid.
 */
export default function LeadPersonProfile({ leadUlid }: { leadUlid: string }) {
  return (
    <Section title="Perfil de persona (CRM)" accent="#e8590c" defaultOpen>
      <CrmConnectGate>
        <LeadPersonProfileInner leadUlid={leadUlid} />
      </CrmConnectGate>
    </Section>
  );
}

function LeadPersonProfileInner({ leadUlid }: { leadUlid: string }) {
  // Envuelto en objeto porque `profile: null` es un resultado VÁLIDO (lead sin
  // perfil) y useCrmResource usa data===null como "aún no cargado".
  const fetchProfile = useCallback(
    () => getPersonProfile(leadUlid).then((profile) => ({ profile })),
    [leadUlid],
  );
  const { data, loading, error, reload, setData } = useCrmResource(
    fetchProfile,
    [leadUlid],
    "No se pudo cargar el perfil de persona del CRM",
  );

  // Error de la última acción (attach/confirm/…): local al panel, no pisa el
  // error de carga. El 409 person_member_elsewhere merece copy propio.
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const runAction = useCallback(
    async (input: PersonActionInput) => {
      setActionBusy(true);
      setActionError(null);
      try {
        const profile = await personAction(leadUlid, input);
        setData({ profile });
      } catch (err) {
        if (err instanceof CrmHttpError) {
          const code = (err.body as { error?: string } | undefined)?.error;
          setActionError(
            code === "person_member_elsewhere"
              ? "Ese lead ya pertenece a otro perfil de persona: recházalo allí antes de vincularlo aquí."
              : code === "person_attach_self"
                ? "No se puede vincular el lead consigo mismo."
                : describeCrmError(err),
          );
        } else {
          setActionError("No se pudo aplicar la acción en el CRM");
        }
      } finally {
        setActionBusy(false);
      }
    },
    [leadUlid, setData],
  );

  if (loading && data === null) {
    return <Muted>Cargando perfil de persona…</Muted>;
  }

  if (error) {
    return (
      <div>
        <div style={errorBox}>{error}</div>
        <button type="button" onClick={reload} style={retryBtn}>
          Reintentar
        </button>
      </div>
    );
  }

  const profile = data?.profile ?? null;

  return (
    <div>
      {actionError && <div style={errorBox}>{actionError}</div>}
      {profile ? (
        <ProfileMembers
          profile={profile}
          leadUlid={leadUlid}
          busy={actionBusy}
          onAction={runAction}
        />
      ) : (
        <Muted>Este lead no pertenece a ningún perfil de persona.</Muted>
      )}
      <AddRelatedForm
        leadUlid={leadUlid}
        existing={profile?.members.map((m) => m.leadId) ?? []}
        busy={actionBusy}
        onAttach={(memberLeadId, kind) =>
          runAction({ action: "attach", memberLeadId, kind })
        }
      />
    </div>
  );
}

function ProfileMembers({
  profile,
  leadUlid,
  busy,
  onAction,
}: {
  profile: PersonProfileView;
  leadUlid: string;
  busy: boolean;
  onAction: (input: PersonActionInput) => void;
}) {
  const [showRejected, setShowRejected] = useState(false);
  // Mismo desglose que el PersonProfileBlock del CRM: misma persona /
  // relacionados / rechazados (estos replegados, son tombstones restaurables).
  const live = profile.members.filter((m) => m.status !== "rejected");
  const same = live.filter((m) => m.kind === "same_person");
  const related = live.filter((m) => m.kind === "related");
  const rejected = profile.members.filter((m) => m.status === "rejected");

  return (
    <div style={{ marginBottom: "0.6rem" }}>
      <div
        style={{ color: "#73879C", fontSize: ".78rem", marginBottom: "0.5rem" }}
      >
        {`${same.length} de la misma persona` +
          (related.length > 0 ? ` · ${related.length} relacionados` : "")}
      </div>

      <MemberGroup
        title="Misma persona"
        members={same}
        leadUlid={leadUlid}
        busy={busy}
        onAction={onAction}
      />
      {related.length > 0 && (
        <MemberGroup
          title="Relacionados"
          members={related}
          leadUlid={leadUlid}
          busy={busy}
          onAction={onAction}
        />
      )}

      {rejected.length > 0 && (
        <div style={{ marginTop: "0.4rem" }}>
          <button
            type="button"
            onClick={() => setShowRejected((v) => !v)}
            style={disclosureBtn}
          >
            {showRejected ? "▾" : "▸"} Rechazados ({rejected.length})
          </button>
          {showRejected &&
            rejected.map((m) => (
              <MemberRow
                key={m.leadId}
                member={m}
                leadUlid={leadUlid}
                busy={busy}
                onAction={onAction}
                rejectedRow
              />
            ))}
        </div>
      )}
    </div>
  );
}

function MemberGroup({
  title,
  members,
  leadUlid,
  busy,
  onAction,
}: {
  title: string;
  members: PersonMemberView[];
  leadUlid: string;
  busy: boolean;
  onAction: (input: PersonActionInput) => void;
}) {
  return (
    <div style={{ marginBottom: "0.5rem" }}>
      <div style={groupTitle}>{title}</div>
      {members.map((m) => (
        <MemberRow
          key={m.leadId}
          member={m}
          leadUlid={leadUlid}
          busy={busy}
          onAction={onAction}
        />
      ))}
    </div>
  );
}

// Fila de miembro: label = nombre ?? email ?? inicio del leadId, email en gris
// al lado. El lead actual y los fusionados van como texto (el fusionado ya no
// tiene ficha propia); el resto linkan a la ficha de Operations. Curación solo
// sobre sugerencias automáticas vivas (mismo criterio que el CRM): confirmar,
// rechazar y —para relacionados— promocionar a misma persona.
function MemberRow({
  member: m,
  leadUlid,
  busy,
  onAction,
  rejectedRow = false,
}: {
  member: PersonMemberView;
  leadUlid: string;
  busy: boolean;
  onAction: (input: PersonActionInput) => void;
  rejectedRow?: boolean;
}) {
  const isCurrent = m.leadId === leadUlid;
  const label = m.fullName || m.email || m.leadId.slice(0, 10);
  const curable =
    m.status === "auto" &&
    !isCurrent &&
    !m.isMergedAway &&
    m.source !== "merge_ledger";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
        flexWrap: "wrap",
        padding: "0.3rem 0",
        fontSize: ".8rem",
        opacity: rejectedRow ? 0.55 : 1,
      }}
    >
      {isCurrent || m.isMergedAway ? (
        <span style={{ fontWeight: isCurrent ? 700 : 500, color: "#1f2933" }}>
          {label}
        </span>
      ) : (
        <Link
          to={`/leads/${m.leadId}`}
          style={{ color: "#0069d9", fontWeight: 500 }}
        >
          {label}
        </Link>
      )}
      {m.fullName && m.email && (
        <span style={{ color: "#adb5bd", fontSize: ".74rem" }}>{m.email}</span>
      )}
      {isCurrent && <Pill label="este lead" scheme="blue" />}
      {m.hasSale && <Pill label="Venta" scheme="green" />}
      {m.isNoned && <Pill label="Noned" scheme="amber" />}
      {m.isMergedAway && <Pill label="Fusionado" scheme="muted" />}
      {m.market && <Pill label={m.market} scheme="muted" />}
      {m.status === "auto" && !rejectedRow && (
        <span style={{ color: "#73879C", fontSize: ".72rem" }}>sugerido</span>
      )}

      <span style={{ flex: 1 }} />

      {rejectedRow ? (
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            onAction({ action: "attach", memberLeadId: m.leadId, kind: m.kind })
          }
          style={miniBtn}
        >
          Restaurar
        </button>
      ) : (
        curable && (
          <>
            <button
              type="button"
              disabled={busy}
              title="Confirmar la sugerencia"
              onClick={() =>
                onAction({ action: "confirm", memberLeadId: m.leadId })
              }
              style={miniBtn}
            >
              ✓
            </button>
            <button
              type="button"
              disabled={busy}
              title="Rechazar la sugerencia"
              onClick={() =>
                onAction({ action: "reject", memberLeadId: m.leadId })
              }
              style={miniBtn}
            >
              ✕
            </button>
            {m.kind === "related" && (
              <button
                type="button"
                disabled={busy}
                title="Promocionar a misma persona"
                onClick={() =>
                  onAction({ action: "promote", memberLeadId: m.leadId })
                }
                style={miniBtn}
              >
                ↑
              </button>
            )}
          </>
        )
      )}
    </div>
  );
}

// "Vincular otro lead": búsqueda debounced contra GET /search (mín. 3 chars),
// filtrando el lead actual y los ya miembros; cada resultado ofrece vincular
// como relacionado o como misma persona (attach manual/confirmed en el CRM).
function AddRelatedForm({
  leadUlid,
  existing,
  busy,
  onAttach,
}: {
  leadUlid: string;
  existing: string[];
  busy: boolean;
  onAttach: (memberLeadId: string, kind: "related" | "same_person") => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<PersonSearchLead[] | null>(null);
  const [searching, setSearching] = useState(false);
  const runId = useRef(0);

  useEffect(() => {
    const myRun = ++runId.current;
    const query = q.trim();
    if (query.length < 3) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchPersonLeads(query);
        if (runId.current !== myRun) return;
        setResults(res);
      } catch {
        if (runId.current !== myRun) return;
        setResults([]);
      } finally {
        if (runId.current === myRun) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const hidden = new Set([leadUlid, ...existing]);
  const visible = results?.filter((r) => !hidden.has(r.id)) ?? null;

  return (
    <div style={{ borderTop: "1px solid #f1f3f5", paddingTop: "0.6rem" }}>
      <div style={groupTitle}>Vincular otro lead</div>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Nombre, email, teléfono o ID…"
        style={searchInput}
      />
      {searching && <Muted>Buscando…</Muted>}
      {visible && visible.length === 0 && !searching && (
        <Muted>Sin resultados.</Muted>
      )}
      {visible?.map((r) => (
        <div
          key={r.id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            flexWrap: "wrap",
            padding: "0.3rem 0",
            fontSize: ".78rem",
          }}
        >
          <span style={{ fontWeight: 500, color: "#1f2933" }}>
            {r.fullName || r.email || r.id.slice(0, 10)}
          </span>
          {r.fullName && r.email && (
            <span style={{ color: "#adb5bd", fontSize: ".72rem" }}>
              {r.email}
            </span>
          )}
          {r.market && <Pill label={r.market} scheme="muted" />}
          <span style={{ flex: 1 }} />
          <button
            type="button"
            disabled={busy}
            onClick={() => onAttach(r.id, "related")}
            style={miniBtn}
          >
            Relacionado
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onAttach(r.id, "same_person")}
            style={miniBtn}
          >
            Misma persona
          </button>
        </div>
      ))}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "#73879C", fontSize: ".82rem" }}>{children}</div>;
}

const groupTitle: React.CSSProperties = {
  fontSize: ".72rem",
  fontWeight: 700,
  color: "#73879C",
  textTransform: "uppercase",
  letterSpacing: ".4px",
  marginBottom: "0.2rem",
};

const errorBox: React.CSSProperties = {
  background: "#fde2e2",
  color: "#b32424",
  padding: "0.5rem 0.7rem",
  borderRadius: "0.4rem",
  fontSize: ".82rem",
  marginBottom: "0.6rem",
};

const retryBtn: React.CSSProperties = {
  padding: "0.35rem 0.75rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#0069d9",
  border: "1px solid #dee2e6",
  fontSize: ".8rem",
  fontWeight: 600,
  cursor: "pointer",
};

const miniBtn: React.CSSProperties = {
  padding: "0.2rem 0.5rem",
  borderRadius: "0.35rem",
  background: "white",
  color: "#0069d9",
  border: "1px solid #dee2e6",
  fontSize: ".72rem",
  fontWeight: 600,
  cursor: "pointer",
};

const disclosureBtn: React.CSSProperties = {
  padding: 0,
  background: "none",
  border: "none",
  color: "#73879C",
  fontSize: ".74rem",
  fontWeight: 600,
  cursor: "pointer",
};

const searchInput: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "0.4rem 0.6rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  fontSize: ".8rem",
  marginBottom: "0.4rem",
};
